import { type TrackModel } from '@/shared/api'
import { showDeleteConfirmationModal } from '@/shared/lib/antd-utils'
import { COMMON_COLUMN_WIDTH } from '@/shared/lib/styling'
import {
  CloseCircleTwoTone,
  DeleteOutlined,
  EditOutlined,
  ExclamationCircleTwoTone,
} from '@ant-design/icons'
import { Button, Flex, Space, type TableProps, Tooltip, theme } from 'antd'
import { useUnit } from 'effector-react'

import { TrackUploadStatus } from '../../lib'
import {
  $areJinglesTableRowsSelected,
  selectedJinglesDeleteRequested,
} from '../../model'
import { jingleDeleted } from '../../model/jingle.model'

interface SortState {
  field: string | null
  order: 'asc' | 'desc' | null
}

interface useJinglesTableColumnsProps {
  onEdit: (jingleId: string) => void
  sort: SortState
}

export const useJinglesTableColumns = ({
  onEdit: handleEdit,
  sort,
}: useJinglesTableColumnsProps): TableProps<TrackModel>['columns'] => {
  const { token: antDesignThemeToken } = theme.useToken()

  const deleteJingle = useUnit(jingleDeleted)

  const deleteSelectedJingles = useUnit(selectedJinglesDeleteRequested)
  const areRowsSelected = useUnit($areJinglesTableRowsSelected)

  const handleDelete = (id: string) => {
    showDeleteConfirmationModal({
      onConfirm: () => {
        deleteJingle({ id })
      },
    })
  }

  const handleDeleteSelected = () => {
    showDeleteConfirmationModal({
      onConfirm: () => {
        deleteSelectedJingles()
      },
      options: {
        title: 'Удаление',
        content: 'Вы уверены, что хотите удалить выбранные записи?',
        okText: 'Удалить',
      },
    })
  }

  const columns: TableProps<TrackModel>['columns'] = [
    {
      title: 'Наименование',
      dataIndex: 'name',
      key: 'name',
      sorter: true,
      sortOrder:
        sort.field === 'name'
          ? sort.order === 'asc'
            ? 'ascend'
            : sort.order === 'desc'
              ? 'descend'
              : null
          : null,
      render: (_, jingle) => {
        if (jingle.uploadStatus === TrackUploadStatus.Uploading) {
          return (
            <Flex gap={16}>
              <Tooltip title="Файл не загружен">
                <ExclamationCircleTwoTone
                  twoToneColor={antDesignThemeToken.colorError}
                />
              </Tooltip>
              {jingle.name}
            </Flex>
          )
        } else if (jingle.uploadStatus === TrackUploadStatus.DeletionPending) {
          return (
            <Flex gap={16}>
              <Tooltip title="Помечен на удаление">
                <CloseCircleTwoTone
                  twoToneColor={antDesignThemeToken.colorWarning}
                />
              </Tooltip>
              {jingle.name}
            </Flex>
          )
        } else return jingle.name
      },
    },
    {
      title: (
        <>
          <Button
            disabled={!areRowsSelected}
            onClick={handleDeleteSelected}
            icon={
              <DeleteOutlined style={areRowsSelected ? { color: 'red' } : {}} />
            }
          />
        </>
      ),
      width: COMMON_COLUMN_WIDTH.ACTIONS,
      key: 'actions',
      align: 'center',
      render: (_, jingle) => (
        <Space size="middle">
          <Button
            onClick={() => {
              handleEdit(jingle.id)
            }}
            icon={<EditOutlined />}
          />
          <Button
            onClick={() => handleDelete(jingle.id)}
            icon={<DeleteOutlined style={{ color: 'red' }} />}
          />
        </Space>
      ),
    },
  ]

  return columns
}
